import { apiClient } from "./client";
import { TipoDispositivo, RiferimentiOdoo } from "../types";

export interface Attivazione extends RiferimentiOdoo {
  id: string;
  in_ritardo?: boolean;
  /** Fase fix 27/07: collegamento alla scheda del Calendario (fonte reale). */
  schedule_id: string | null;
  cliente_id: string | null;
  centro_assistenza_id: string | null;
  cliente_nome: string | null;
  sede: string | null;
  /** Sede specifica dell'anagrafica (propedeutico alla vista aggregata per sede). */
  sede_id: string | null;
  tipo_dispositivo: TipoDispositivo | null;
  marca: string | null;
  modello: string | null;
  dispositivo_matricola: string | null;
  dispositivo_catalogo_id: string | null;
  /**
   * Il pezzo FISICO, ricavato dalla matricola per corrispondenza esatta. Non si
   * invia dal form — la rotta non ha mai accettato una chiave dal body — ma
   * serve in lettura per riaprire il record sapendo se e' gia' agganciato.
   */
  device_id: string | null;
  data_attivazione: string | null;
  /**
   * Orario dell'APPUNTAMENTO — accompagna `data_attivazione`, non la data di
   * esecuzione. Colonne `TIME`: arrivano come `"1970-01-01T09:30:00.000Z"` e
   * si leggono affettando la stringa, mai con `new Date().getHours()`. Vedi
   * `utils/orario.ts`.
   */
  orario_inizio: string | null;
  orario_fine: string | null;
  effettuata: boolean;
  data_effettiva: string | null;
  tipo_attivazione: string | null;
  vigilanza: string | null;
  istituto_vigilanza_id: string | null;
  tipo_servizio: string | null;
  centro_assistenza_vigilanza_id: string | null;
  comunicazione_tipo: "LAN" | "SIM" | "Nessuna" | null;
  lan_dhcp: boolean;
  lan_ip: string | null;
  lan_subnet: string | null;
  lan_gateway: string | null;
  lan_dns: string | null;
  /** DNS secondario. La colonna primaria resta `lan_dns` senza l'1. */
  lan_dns2: string | null;
  sim_numero: string | null;
  sim_operatore: string | null;
  tecnico_attivazione: string | null;
  /** Persona incaricata (FK utente); null = non assegnata a nessuno in particolare. */
  assegnato_utente_id: string | null;
  stato: string;
  note: string | null;
}

export interface ActivationFilters {
  istituto_vigilanza_id?: string;
  /** Ricerca testuale della barra dell'elenco. */
  search?: string;
  stato?: string;
  mese?: number; // 1-12
  anno?: number;
  ritardo?: boolean;
}

export async function listAttivazioni(filters: ActivationFilters = {}): Promise<{ records: Attivazione[] }> {
  const { data } = await apiClient.get<{ records: Attivazione[] }>("/attivazioni", { params: filters });
  return data;
}

/**
 * Il payload porta anche `censisci_device` e `censimento_tipo_possesso`, che
 * NON sono colonne di `attivazioni`: la rotta le legge dal body grezzo per
 * decidere se censire il pezzo fisico che la matricola nomina.
 */
export type AttivazionePayload = Partial<Attivazione> & {
  censisci_device?: boolean;
  censimento_tipo_possesso?: string | null;
};

/**
 * Esito del censimento chiesto con la spunta. `null` quando la spunta non
 * c'era, o quando la matricola corrispondeva già a un pezzo censito — in quel
 * caso l'attivazione si aggancia e basta, senza creare niente.
 */
export interface EsitoCensimentoDevice {
  device_id: string;
  matricola: string;
  creato: boolean;
  relazione_creata: boolean;
}

export async function createAttivazione(
  payload: AttivazionePayload
): Promise<{ record: Attivazione; censimento: EsitoCensimentoDevice | null }> {
  const { data } = await apiClient.post<{ record: Attivazione; censimento: EsitoCensimentoDevice | null }>(
    "/attivazioni",
    payload
  );
  return data;
}

export async function updateAttivazione(
  id: string,
  payload: AttivazionePayload
): Promise<{ record: Attivazione; censimento: EsitoCensimentoDevice | null }> {
  const { data } = await apiClient.patch<{ record: Attivazione; censimento: EsitoCensimentoDevice | null }>(
    `/attivazioni/${id}`,
    payload
  );
  return data;
}

/**
 * Eliminazione definitiva, riservata all'admin. Il backend risponde 409 con
 * `dettaglio` (conteggio degli allegati collegati) se l'attivazione non è
 * eliminabile: quel corpo di errore va propagato al chiamante così com'è.
 */
export async function deleteAttivazione(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/attivazioni/${id}`);
  return data;
}
